import { useCallback, useState } from "react";
import { userReportsApi } from "../api";
import { getApiErrorMessage } from "../utils";

interface UseReportSubscriptionOptions {
  reportId: string;
  isSubscribed: boolean;
  onChange: () => void | Promise<void>;
}

export function useReportSubscription({
  reportId,
  isSubscribed,
  onChange,
}: UseReportSubscriptionOptions) {
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");

  const setSubscribed = useCallback(async (subscribe: boolean) => {
    setSubscriptionError("");
    setSubscriptionLoading(true);
    try {
      if (subscribe) await userReportsApi.subscribe(reportId);
      else await userReportsApi.unsubscribe(reportId);
      await onChange();
    } catch (caught) {
      setSubscriptionError(
        getApiErrorMessage(caught, "Could not update your subscription"),
      );
    } finally {
      setSubscriptionLoading(false);
    }
  }, [onChange, reportId]);

  const toggleSubscription = useCallback(
    () => setSubscribed(!isSubscribed),
    [isSubscribed, setSubscribed],
  );
  const unsubscribe = useCallback(
    () => setSubscribed(false),
    [setSubscribed],
  );

  return {
    subscriptionLoading,
    subscriptionError,
    toggleSubscription,
    unsubscribe,
  };
}
